import { ArrowLeft, ArrowRight, Award, X, Check, XCircle } from 'lucide-react';
import { useState, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useTranslations } from '@/hooks/use-translations';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useCasesConfig } from '@/modules/case/hooks/useCasesConfig';
import { captureSentry } from '@/utils/sentryHelper';

import { CertificationQuestion } from '../types';

interface CertificationReviewProps {
  caseId: string;
  caseTitle: string;
  userId: string;
  onClose: () => void;
}

export const CertificationReview = ({
  caseId,
  caseTitle,
  userId,
  onClose,
}: CertificationReviewProps) => {
  const { t } = useTranslations();
  const { getCaseMetadata, casesConfig } = useCasesConfig();
  const [questions, setQuestions] = useState<CertificationQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (casesConfig) {
      void loadReviewData();
    }
  }, [caseId, userId, casesConfig]);

  const loadReviewData = async () => {
    try {
      // Load questions from external API
      const caseMetadata = getCaseMetadata(caseId);

      if (!caseMetadata) {
        console.error('Case metadata not loaded yet');
        return;
      }

      if (!caseMetadata.certificationUrl) {
        throw new Error(`No certification URL found for case ${caseId}`);
      }

      const response = await fetch(caseMetadata.certificationUrl);

      if (!response.ok) {
        throw new Error(`Failed to load certification: ${response.statusText}`);
      }

      const certificationData = await response.json();

      const formattedQuestions = certificationData.certification_questions.map((q: any) => ({
        id: q.id,
        case_id: certificationData.case_id,
        question_text: q.question_text,
        options: q.options,
        correct_answer: q.correct_answer,
        explanation: q.explanation,
      }));

      setQuestions(formattedQuestions);

      // Load the best passed attempt
      const { data: bestAttempt, error: attemptError } = await supabase
        .from('certification_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .eq('passed', true)
        .not('completed_at', 'is', null)
        .order('score', { ascending: false })
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (attemptError) throw attemptError;

      if (!bestAttempt) {
        throw new Error('No passed certification attempt found');
      }

      const savedAnswers = Array.isArray(bestAttempt.answers)
        ? bestAttempt.answers.map((a: any) => (typeof a === 'number' ? a : -1))
        : [];

      setUserAnswers(savedAnswers);
    } catch (error) {
      console.error('Error loading certification review:', error);
      captureSentry({
        title: 'Failed to load certification review',
        error,
        detail: { caseId, userId },
      });
      toast({
        title: t('validation.errors.loadingCertification'),
        description: 'Unable to load certification review',
        variant: 'destructive',
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Card className="p-8">
          <p className="font-body text-lg">{t('certification.test.loadingTest')}</p>
        </Card>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="max-h-[90vh] w-full max-w-3xl overflow-y-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-heading text-3xl text-livelinx-purple200">
            <Award className="mr-2 inline h-8 w-8" />
            {t('certification.review.title', { caseTitle })}
          </h2>
          <Button variant="ghost" onClick={onClose} size="icon">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="mb-6">
          <div className="mb-2 flex justify-between text-sm text-livelinx-grey200">
            <span>
              {t('certification.test.questionProgress', {
                current: currentQuestionIndex + 1,
                total: questions.length,
              })}
            </span>
            <span>
              {t('certification.test.progressComplete', {
                percent: progress.toFixed(0),
              })}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="mb-8">
          <h3 className="mb-4 font-body text-xl text-livelinx-text">
            {currentQuestion?.question_text}
          </h3>

          <div className="space-y-3">
            {currentQuestion?.options.map((option, index) => {
              const isCorrect = index === currentQuestion.correct_answer;
              const isUserAnswer = index === userAnswers[currentQuestionIndex];
              const isWrong = isUserAnswer && !isCorrect;

              return (
                <div
                  key={index}
                  className={cn(
                    'mb-3 flex items-center space-x-3 rounded-lg border-2 p-4 transition-all',
                    isCorrect && 'border-livelinx-green bg-livelinx-green50',
                    isWrong && 'border-livelinx-red bg-livelinx-red/10',
                    !isCorrect && !isWrong && 'border-livelinx-lightgray100 bg-white'
                  )}
                >
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                    {isCorrect && <Check className="h-5 w-5 text-livelinx-green" />}
                    {isWrong && <XCircle className="h-5 w-5 text-livelinx-red" />}
                  </div>
                  <Label
                    className={cn(
                      'flex-1 cursor-default font-body text-base',
                      isCorrect && 'text-livelinx-green200',
                      isWrong && 'text-livelinx-red'
                    )}
                  >
                    {option}
                  </Label>
                </div>
              );
            })}
          </div>

          {currentQuestion?.explanation && (
            <div className="mt-6 rounded-lg border border-livelinx-blue300 bg-livelinx-blue50 p-4">
              <p className="font-body text-sm text-livelinx-text">
                <strong className="text-livelinx-blue300">
                  {t('certification.review.explanation')}:
                </strong>{' '}
                {currentQuestion.explanation}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-livelinx-lightgray100 pt-4">
          <Button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            variant="outline"
            className="border-livelinx-purple200 text-livelinx-purple200"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.buttons.previous')}
          </Button>

          {currentQuestionIndex === questions.length - 1 ? (
            <Button
              onClick={onClose}
              className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
            >
              {t('common.buttons.close')}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-livelinx-purple200 text-white hover:bg-livelinx-purple400"
            >
              {t('common.buttons.next')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};
